import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Mail, Phone, CheckCircle, AlertCircle, Info } from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const VerificationPage = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [phoneCode, setPhoneCode] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [loading, setLoading] = useState({ email: false, phone: false });
  const [error, setError] = useState({ email: "", phone: "" });
  const [whatsappEnabled, setWhatsappEnabled] = useState(false);

  useEffect(() => {
    // Get stored email and phone from intent form
    const storedEmail = localStorage.getItem("verification_email");
    const storedPhone = localStorage.getItem("verification_phone");
    
    if (storedEmail) setEmail(storedEmail);
    if (storedPhone) setPhone(storedPhone);
    
    // Check if WhatsApp verification is enabled (it's not in current implementation)
    // This would normally come from a settings API
    setWhatsappEnabled(false);
  }, []);

  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setLoading(prev => ({ ...prev, email: true }));
    setError(prev => ({ ...prev, email: "" }));

    try {
      await axios.post(`${API}/auth/verify-email?email=${encodeURIComponent(email)}&code=${emailCode}`);
      setEmailVerified(true);
    } catch (err) {
      setError(prev => ({ 
        ...prev, 
        email: err.response?.data?.detail || "Invalid verification code" 
      }));
    } finally {
      setLoading(prev => ({ ...prev, email: false }));
    }
  };

  const handleVerifyPhone = async (e) => {
    e.preventDefault();
    if (!whatsappEnabled) return;
    
    setLoading(prev => ({ ...prev, phone: true }));
    setError(prev => ({ ...prev, phone: "" }));

    try {
      await axios.post(`${API}/auth/verify-phone?phone=${encodeURIComponent(phone)}&code=${phoneCode}`);
      setPhoneVerified(true);
    } catch (err) {
      setError(prev => ({ 
        ...prev, 
        phone: err.response?.data?.detail || "Invalid verification code" 
      }));
    } finally {
      setLoading(prev => ({ ...prev, phone: false }));
    }
  };

  const handleContinue = () => {
    // Clear stored verification data
    localStorage.removeItem("verification_email");
    localStorage.removeItem("verification_phone");
    navigate("/login");
  };

  // Email verification is required, WhatsApp is optional
  const canProceed = emailVerified;

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4" data-testid="verification-page">
      <div className="max-w-xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-heading font-bold text-3xl text-slate-900 mb-2">
            Verify Your Contact Details
          </h1>
          <p className="text-slate-600">
            Complete email verification to proceed with your registration
          </p>
        </div>

        <div className="space-y-6">
          {/* Email Verification - Required */}
          <Card className={`border ${emailVerified ? 'border-teal-500 bg-teal-50/50' : 'border-slate-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  emailVerified ? 'bg-teal-100' : 'bg-slate-100'
                }`}>
                  {emailVerified ? (
                    <CheckCircle className="w-5 h-5 text-teal-700" />
                  ) : (
                    <Mail className="w-5 h-5 text-slate-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-slate-900">
                      Email Verification
                    </h3>
                    <Badge className="bg-red-100 text-red-700 text-xs">Required</Badge>
                  </div>
                  <p className="text-sm text-slate-500">{email || "your@email.com"}</p>
                </div>
              </div>

              {emailVerified ? (
                <div className="flex items-center gap-2 text-teal-700">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">Email verified successfully!</span>
                </div>
              ) : (
                <form onSubmit={handleVerifyEmail}>
                  {error.email && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error.email}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Label htmlFor="emailCode" className="sr-only">
                        Email Verification Code
                      </Label>
                      <Input
                        id="emailCode"
                        placeholder="Enter 6-digit code"
                        value={emailCode}
                        onChange={(e) => setEmailCode(e.target.value)}
                        maxLength={6}
                        className="h-11"
                        data-testid="email-code-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={loading.email || emailCode.length < 6}
                      className="bg-slate-900 text-white hover:bg-slate-800"
                      data-testid="verify-email-btn"
                    >
                      {loading.email ? "Verifying..." : "Verify"}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-2">
                    Check your inbox for the verification code. It may take a few minutes.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>

          {/* WhatsApp Verification - Optional */}
          <Card className={`border ${phoneVerified ? 'border-teal-500 bg-teal-50/50' : 'border-slate-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                  phoneVerified ? 'bg-teal-100' : 'bg-slate-100'
                }`}>
                  {phoneVerified ? (
                    <CheckCircle className="w-5 h-5 text-teal-700" />
                  ) : (
                    <Phone className="w-5 h-5 text-slate-600" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-heading font-semibold text-slate-900">
                      WhatsApp Verification (Optional)
                    </h3>
                    <Badge className="bg-slate-100 text-slate-600 text-xs">Optional</Badge>
                  </div>
                  <p className="text-sm text-slate-500">{phone || "+91 XXXXX XXXXX"}</p>
                </div>
              </div>

              {phoneVerified ? (
                <div className="flex items-center gap-2 text-teal-700">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">WhatsApp verified successfully!</span>
                </div>
              ) : !whatsappEnabled ? (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-slate-500 mt-0.5" />
                    <div>
                      <p className="text-sm text-slate-600 font-medium">
                        WhatsApp verification is not currently enabled.
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Add and verify a WhatsApp number for future CPSI communication.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleVerifyPhone}>
                  {error.phone && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error.phone}</AlertDescription>
                    </Alert>
                  )}
                  
                  <div className="flex gap-3">
                    <div className="flex-1">
                      <Label htmlFor="phoneCode" className="sr-only">
                        WhatsApp Verification Code
                      </Label>
                      <Input
                        id="phoneCode"
                        placeholder="Enter 6-digit code"
                        value={phoneCode}
                        onChange={(e) => setPhoneCode(e.target.value)}
                        maxLength={6}
                        className="h-11"
                        data-testid="phone-code-input"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      disabled={loading.phone || phoneCode.length < 6}
                      className="bg-slate-900 text-white hover:bg-slate-800"
                      data-testid="verify-phone-btn"
                    >
                      {loading.phone ? "Verifying..." : "Verify"}
                    </Button>
                  </div>
                  
                  <p className="text-xs text-slate-500 mt-2">
                    Check your WhatsApp for the verification code.
                  </p>
                </form>
              )}
            </CardContent>
          </Card>

          {/* Continue Button - Only requires email verification */}
          {canProceed && (
            <Card className="border border-teal-500 bg-teal-50">
              <CardContent className="p-6 text-center">
                <CheckCircle className="w-12 h-12 text-teal-700 mx-auto mb-3" />
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">
                  Email Verification Complete!
                </h3>
                <p className="text-slate-600 mb-4">
                  Your email has been verified. You can now login and complete your full membership application.
                  {!phoneVerified && " WhatsApp verification can be completed later."}
                </p>
                <Button 
                  onClick={handleContinue}
                  className="bg-teal-700 text-white hover:bg-teal-600"
                  data-testid="continue-to-login"
                >
                  Continue to Login
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Help Notice */}
          {!canProceed && (
            <p className="text-center text-sm text-slate-500">
              Having trouble? Contact{" "}
              <a href="mailto:office@cpsindia.org.in" className="text-teal-700 hover:text-teal-800">
                office@cpsindia.org.in
              </a>
              {" "}or WhatsApp{" "}
              <a href="https://wa.me/919788722666" className="text-teal-700 hover:text-teal-800">
                +91 9788722666
              </a>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerificationPage;
